import { useState } from 'react';
import { cn } from '../../utils/cn.js';
import { TeamSelector } from '../TeamSelector.js';
import { BrandMark } from './BrandMark.js';
import { CommandSearch } from './CommandSearch.js';
import { MenuIcon, ThreeDots } from './shell-icons.js';
import { ThemeToggle } from './ThemeToggle.js';
import { UserMenu } from './UserMenu.js';
import { WaitingOnMeMenu } from './WaitingOnMeMenu.js';

/**
 * Template example: NextAdmin `components/common/header/index.tsx` (commit e0b72a9aa69e).
 * Below xl: menu | brand | dots, and the dots open a row with the search and the actions.
 * From xl: the page title on the left (owner 2026-10-17: the header and the page header conflicted), the search icon and
 * the actions on the right. `CommandSearch` renders once because it owns the global ⌘K listener, so the second layout
 * is the same elements re-flowed by `xl:contents`.
 */
export function ShellHeader({
  onOpenNavigation,
  titleSlotRef,
}: {
  onOpenNavigation: () => void;
  titleSlotRef: (element: HTMLDivElement | null) => void;
}) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 w-full border-b-[0.5px] border-card-border bg-card-surface-area px-2 py-4 lg:px-5">
      <div className="flex flex-wrap items-center xl:flex-nowrap xl:justify-between">
        {/* Left (< xl): menu / hamburger */}
        <div className="flex flex-1 justify-start xl:hidden">
          <button
            type="button"
            id="mobile-menu-toggle"
            onClick={onOpenNavigation}
            aria-label="Open sidebar menu"
            className="rounded-md px-1.5 py-1 text-icon-tertiary transition-colors hover:text-text-primary"
          >
            <MenuIcon />
          </button>
        </div>

        {/* Centre (< xl): brand */}
        <div className="flex items-center justify-center xl:hidden">
          <BrandMark />
        </div>

        {/* Right (< xl): three-dot */}
        <div className="flex flex-1 justify-end xl:hidden">
          <button
            type="button"
            id="mobile-info-toggle"
            onClick={() => setIsDrawerOpen(!isDrawerOpen)}
            aria-label="Open quick access"
            aria-expanded={isDrawerOpen}
            className={cn(
              'rounded-md px-1.5 py-3 transition-colors',
              isDrawerOpen
                ? 'bg-background-gray-secondary text-text-primary'
                : 'text-icon-tertiary hover:text-text-primary',
            )}
          >
            <ThreeDots />
          </button>
        </div>

        {/* Quick access row (< xl, when open) and the desktop header contents (xl+) */}
        <div
          className={cn(
            'basis-full items-center justify-end gap-2.5 px-3 pt-4 xl:contents',
            isDrawerOpen ? 'flex' : 'hidden',
          )}
        >
          {/* The page's title (PageTitle renders into this on xl+); the search sits with the actions as an icon. */}
          <div ref={titleSlotRef} className="hidden h-10 min-w-0 flex-1 items-center pr-4 xl:flex" />
          <div className="flex items-center gap-2.5">
            <CommandSearch />
            <TeamSelector />
            <ThemeToggle />
            <WaitingOnMeMenu />
            <UserMenu />
          </div>
        </div>
      </div>
    </header>
  );
}
